import { isHotkey as isHotkeyLib } from 'is-hotkey'
import type { HotKeyOptions, KeyboardEventLike } from 'is-hotkey'

/**
 * Detect whether the platform uses Meta (Mac/iOS) or Control as the primary modifier.
 *
 * `is-hotkey` checks `navigator.platform` at module load time, but WebKit on Linux
 * incorrectly reports `MacIntel` for `navigator.platform`. We use `navigator.userAgentData`
 * (when available) with a `navigator.userAgent` fallback for a more reliable check.
 */
const isMacPlatform = () => {
  if (typeof window === 'undefined') {
    return false
  }
  const uad = (navigator as unknown as Record<string, unknown>).userAgentData as { platform?: string } | undefined
  if (uad?.platform) {
    return /mac|ios/i.test(uad.platform)
  }
  return /Mac|iPod|iPhone|iPad/.test(navigator.userAgent)
}

const IS_MAC = isMacPlatform()
const MOD = IS_MAC ? 'meta' : 'control'

const resolveMod = (hotkey: string) => hotkey.replace(/\bmod\b/gi, MOD)

const resolveHotkeys = (hotkey: string | readonly string[]): string | readonly string[] =>
  Array.isArray(hotkey) ? (hotkey as string[]).map(resolveMod) : resolveMod(hotkey as string)

/** @note we wrap this to resolve `mod` with reliable platform detection */
export const isHotkey = (hotkey: string | readonly string[], options?: HotKeyOptions | undefined) =>
  isHotkeyLib(resolveHotkeys(hotkey), options) as (ev: KeyboardEventLike) => boolean

/**
 * Native controls that consume keystrokes as text entry or selection. A
 * keydown from one of these belongs to the control, not to a hotkey.
 */
const textEntryTags = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

/**
 * True when a keydown originated inside a native text-entry control.
 *
 * GH #1806: hotkey dispatchers listen on `document.body` or on the editor's
 * host element and call `preventDefault()` on a match. Keydown from a focused
 * `<input>`/`<textarea>` bubbles all the way up, so without this check an
 * unscoped binding swallows the key inside every text control in the app. The
 * manifest binds `tab`, which made that a WCAG 2.1.2 keyboard trap: focus
 * could not leave the control at all.
 *
 * Deliberately does NOT treat `contentEditable` as text entry. Slate's editing
 * surface is a contentEditable `<div>` and is exactly where editor hotkeys are
 * meant to fire — widening this predicate to "editable" would disable bold,
 * italic and block-move everywhere. The trap is a *nested* native control.
 *
 * Duck-typed on `tagName` rather than `instanceof HTMLElement` so it also
 * holds for events crossing realms (iframes) and for synthetic events in
 * non-DOM test environments.
 */
export const isTextEntryTarget = (target: EventTarget | null | undefined): boolean => {
  if (!target || typeof target !== 'object' || !('tagName' in target)) {
    return false
  }
  const { tagName } = target as { tagName: unknown }
  return typeof tagName === 'string' && textEntryTags.has(tagName.toUpperCase())
}
